import React, { useMemo, useState } from 'react';
import { useTranslation } from '../../utils/i18nStub';

const LANGUAGE_OPTIONS = [
  { value: 'ar', label: 'اللغة العربية' },
  { value: 'en', label: 'اللغة الإنجليزية' },
  { value: 'fr', label: 'اللغة الفرنسية' },
  { value: 'ur', label: 'اللغة الأردية' },
  { value: 'hi', label: 'اللغة الهندية' },
  { value: 'ph', label: 'لغة التاغالوغ' },
  { value: 'id', label: 'اللغة الإندونيسية' },
  { value: 'bn', label: 'اللغة البنغالية' },
];

const EmployeeLanguagesInfo = () => {
  const { t } = useTranslation('languages');
  const [selected, setSelected] = useState(['ar']);
  const [levelMap, setLevelMap] = useState({ ar: 'native' });
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState({ selected: ['ar'], levelMap: { ar: 'native' } });

  const handleAddLanguage = (event) => {
    const value = event.target.value;
    if (!value || selected.includes(value)) return;
    setSelected((prev) => [...prev, value]);
    setLevelMap((prev) => ({ ...prev, [value]: 'intermediate' }));
  };

  const removeLanguage = (value) => {
    setSelected((prev) => prev.filter((lang) => lang !== value));
    setLevelMap((prev) => {
      const next = { ...prev };
      delete next[value];
      return next;
    });
  };

  const updateLevel = (value, level) => {
    setLevelMap((prev) => ({ ...prev, [value]: level }));
  };

  const availableOptions = useMemo(
    () => LANGUAGE_OPTIONS.filter((option) => !selected.includes(option.value)),
    [selected]
  );

  return (
    <div className="space-y-6 text-right" dir="rtl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {t('languages.title', 'اللغات التي تتقنها')}
        </h2>
        {!isEditing ? (
          <button
            type="button"
            onClick={() => {
              setSnapshot({ selected: [...selected], levelMap: { ...levelMap } });
              setIsEditing(true);
            }}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setSnapshot({ selected: [...selected], levelMap: { ...levelMap } });
                setIsEditing(false);
              }}
              className="rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors"
            >
              {t('save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={() => {
                setSelected([...snapshot.selected]);
                setLevelMap({ ...snapshot.levelMap });
                setIsEditing(false);
              }}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">

        <div className="flex flex-col md:flex-row items-start gap-4">
          <div className="flex-1">
            <label className="text-sm font-semibold text-gray-500 mb-2 block">
              {t('languages.add_language', 'اختر لغة للإضافة')}
            </label>
            <select
              className="w-full md:w-auto rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              onChange={handleAddLanguage}
              value=""
              disabled={!isEditing}
            >
              <option value="">{t('languages.select_placeholder', 'اختر اللغة')}</option>
              {availableOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-sa-50 border border-sa-100 rounded-2xl px-4 py-3 text-sm text-sa-800">
            {t('languages.note', 'يمكنك إضافة أكثر من لغة من القائمة وتحديد مستوى كل لغة على حدة.')}
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {selected.map((value) => {
            const option = LANGUAGE_OPTIONS.find((opt) => opt.value === value);
            if (!option) return null;

            return (
              <article
                key={value}
                className="border border-gray-200 rounded-2xl px-4 py-3 flex flex-col md:flex-row md:items-center gap-4 bg-gray-50"
              >
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900">{option.label}</h3>
                  <p className="text-sm text-gray-500">{t('languages.level_label', 'حدد مستوى إتقانك لهذه اللغة')}</p>
                </div>

                <div className="flex items-center gap-3">
                  <select
                    className="rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                    value={levelMap[value] || 'intermediate'}
                    onChange={(event) => updateLevel(value, event.target.value)}
                    disabled={!isEditing}
                  >
                    <option value="native">{t('languages.level_native', 'لغة الأم')}</option>
                    <option value="advanced">{t('languages.level_advanced', 'متقدم')}</option>
                    <option value="intermediate">{t('languages.level_intermediate', 'متوسط')}</option>
                    <option value="basic">{t('languages.level_basic', 'مبتدئ')}</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => removeLanguage(value)}
                    className="inline-flex items-center justify-center rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                    disabled={!isEditing}
                  >
                    {t('languages.remove', 'إزالة')}
                  </button>
                </div>
              </article>
            );
          })}

          {selected.length === 0 && (
            <div className="text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-2xl py-10">
              {t('languages.empty', 'لم يتم إضافة لغات بعد. استخدم القائمة أعلاه لاختيار لغة.')}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EmployeeLanguagesInfo;
